import type en from '../en/settings'
import type { Translation } from '../types'

/** Settings */
const settings: Translation<typeof en> = {
  "settings.mail.sent": "Testmail verstuurd naar {to} via {host}. Uw eigen mailserver is vanaf nu in gebruik.",
  "settings.mail.testFailed": "De testmail kon niet worden verstuurd",
  "settings.mail.inUse": "In gebruik: uw eigen mailserver.",
  "settings.mail.notYet": "Nog niet in gebruik: stuur een testmail om uw eigen mailserver te gaan gebruiken. Tot die tijd gaat mail via de mailserver van het platform.",
  "settings.mail.sendTest": "Testmail versturen",
  "settings.mail.testHint": "Gaat naar uw eigen adres, via de opgeslagen instellingen: sla uw wijzigingen eerst op.",
  "settings.saved": "Instellingen opgeslagen",
  "settings.saveFailed": "Kon de instellingen niet opslaan",
  "settings.companySaved": "Bedrijfsgegevens opgeslagen",
  "settings.companySaveFailed": "Kon de bedrijfsgegevens niet opslaan",
  "settings.licenseRenewed": "Licentie verlengd!",
  "settings.licenseInvalid": "Ongeldige of al gebruikte licentiecode",
  "settings.title": "Bedrijfsinstellingen",
  "settings.save": "Instellingen opslaan",
  "settings.companyInfo": "Bedrijfsgegevens",
  "settings.companyName": "Bedrijfsnaam",
  "settings.customDomain": "Eigen domein",
  "settings.customDomainHint": "Het adres waarop mensen inloggen onder uw eigen merk. Wijs na het opslaan de DNS van het domein naar dit platform en bewijs met een TXT-record (hier getoond) dat het van u is. Het HTTPS-certificaat regelt het platform zelf.",
  "settings.saveCompany": "Bedrijfsgegevens opslaan",
  "settings.signIns": "Inloggen",
  "settings.sessionDays": "Maximale sessieduur (dagen)",
  "settings.sessionDaysHelp": "Daarna logt iedereen in het bedrijf opnieuw in: 1 dag tot 6 maanden (183 dagen), standaard 7. Gerekend vanaf het inloggen; een wijziging geldt ook voor de sessies die nu lopen.",
  "settings.idleMax": "Uitloggen na een periode zonder activiteit: hooguit",
  "settings.idleHelp": "Een sessie die zo lang niet wordt gebruikt, wordt uitgelogd (standaard 8 uur). Iedereen kan onder Mijn profiel een kortere tijd kiezen.",
  "settings.idleOffWarning": "Hiermee kunnen mensen een van de accountbeveiligingen voor dit bedrijf uitschakelen: een sessie die openstaat op een onbeheerde computer blijft dan ingelogd tot zij haar maximale duur bereikt.",
  "settings.idleOffQuestion": "Mensen toestaan dit uit te schakelen?",
  "settings.idleOffLabel": "Mensen zelf het uitloggen na inactiviteit laten uitschakelen",
  "settings.syncInterval": "Synchronisatie-interval",
  "settings.dataPeriod": "Standaardperiode voor gegevens",
  "settings.retention": "Bewaartermijn van gegevens",
  "settings.automation": "Automatisering en meldingen",
  "settings.autoApply": "Standaarden automatisch toepassen op nieuwe locaties",
  "settings.autoApplySub": "Pas het standaardsjabloon van het bedrijf automatisch toe wanneer een locatie verbinding maakt",
  "settings.emailAlerts": "E-mailmeldingen",
  "settings.emailAlertsSub": "Stuur meldingsmails naar de bedrijfsbeheerders als locaties offline gaan of er kritieke meldingen afgaan",
  "settings.webhookAlerts": "Webhookmeldingen",
  "settings.webhookAlertsSub": "Meldingsgegevens via POST naar de ingestelde webhook-URL’s sturen",
  "settings.smtp.title": "Eigen e-mail / SMTP",
  "settings.smtp.intro": "Meldingen en mails met VPN-configuratie gaan via uw eigen mailserver, vanaf uw eigen adres. Uitnodigingen en wachtwoordherstel komen altijd van het platform, zodat geen mailserver daarbuiten ooit een inloglink ziet. Laat leeg om voor alles het platform te gebruiken.",
  "settings.smtp.host": "SMTP-host",
  "settings.smtp.userPlaceholder": "u@domein.nl",
  "settings.smtp.keepPlaceholder": "(leeg laten om te behouden)",
  "settings.smtp.passwordPlaceholder": "SMTP-wachtwoord",
  "settings.smtp.encrypted": "Versleuteld met AES-256 — nooit teruggestuurd naar de browser",
  "settings.smtp.fromEmail": "Afzenderadres",
  "settings.smtp.fromEmailPlaceholder": "hallo@uwbedrijf.nl",
  "settings.smtp.fromName": "Afzendernaam",
  "settings.overview": "Bedrijfsoverzicht",
  "settings.slug": "Slug",
  "settings.created": "Aangemaakt",
  "settings.brandingText": "Pas het uiterlijk van uw platform aan: kleuren, logo, appnaam, lettertypen en eigen CSS.",
  "settings.openBranding": "Huisstijleditor openen →",
  "settings.license": "Licentie",
  "settings.expires": "Verloopt",
  "settings.maxSites": "Max. locaties",
  "settings.maxUsers": "Max. gebruikers",
  "settings.renewLicense": "Licentie verlengen",
  "settings.licenseCode": "Licentiecode",
  "settings.renew": "Verlengen",
  "settings.more": "Meer instellingen",
  "settings.link.branding": "Huisstijl en uiterlijk",
  "settings.link.vpn": "VPN-configuratie",
  "settings.link.users": "Gebruikers en rollen",
  "settings.dns.ok": "Aanwezig",
  "settings.dns.missing": "Ontbreekt",
  "settings.dns.update": "Moet worden gewijzigd",
  "settings.dns.unknown": "Kon niet controleren",
  "settings.dns.title": "DNS-records voor mail van {domain}",
  "settings.dns.freeMail": "{domain} is van een mailprovider: niemand anders dan die provider kan daar records toevoegen, dus ontvangers kunnen deze mail als spam behandelen. Gebruik een adres op uw eigen domein.",
  "settings.dns.addRecords": "Voeg deze TXT-records toe bij de DNS-provider van {domain}, zodat ontvangende mailservers de mail accepteren in plaats van haar als spam te markeren.",
  "settings.dns.copyName": "Naam van het {type}-record kopiëren",
  "settings.dns.copyValue": "Waarde van het {type}-record kopiëren",
  "settings.dns.now": "Nu:",
  "settings.checkNow": "Nu controleren",
  "settings.domain.proven": "Domein bewezen",
  "settings.domain.serves": "{domain} toont nu uw dashboard.",
  "settings.domain.notFound": "Nog niet gevonden",
  "settings.domain.dnsSlow": "DNS-wijzigingen kunnen even duren — probeer het over een paar minuten opnieuw.",
  "settings.domain.checkFailed": "Controle mislukt",
  "settings.domain.ownershipProven": "Eigendom bewezen",
  "settings.domain.ownershipNotYet": "Eigendom nog niet bewezen",
  "settings.domain.dnsHere": "DNS wijst hierheen",
  "settings.domain.dnsNotYet": "DNS wijst nog niet hierheen",
  "settings.domain.addTxt": "Voeg dit TXT-record toe bij uw DNS-provider. Tot het is gevonden, toont {domain} een pagina \"wordt ingericht\"",
  "settings.domain.releaseBefore": "; als het niet uiterlijk ",
  "settings.domain.releaseAfter": " is gevonden, wordt het domein losgekoppeld van uw bedrijf",
  "settings.domain.copyName": "Naam van het record kopiëren",
  "settings.domain.copyValue": "Waarde van het record kopiëren",
  "settings.checked": " · gecontroleerd {when}",
  "settings.domain.pointA": "Laat ook een A-record voor {domain} wijzen naar {ip}.",
  "settings.domain.thisServer": "deze server",
  "settings.sso.saved": "Single sign-on opgeslagen",
  "settings.sso.intro": "Mensen loggen in met uw eigen identiteitsprovider (OpenID Connect: Microsoft Entra ID, Google Workspace, Okta, Keycloak en andere). Diens meerstapsverificatie vervangt die van ons. Registreer deze terugkeeradressen voor het inloggen bij de provider:",
  "settings.sso.copy": "{url} kopiëren",
  "settings.sso.issuer": "Issuer",
  "settings.sso.clientId": "Client-ID",
  "settings.sso.clientSecret": "Clientgeheim",
  "settings.sso.secretStored": "Opgeslagen — typ een nieuwe om het te vervangen",
  "settings.sso.on": "Single sign-on staat aan",
  "settings.sso.autoJoin": "Mensen van de bewezen domeinen worden bij hun eerste keer inloggen lid van het bedrijf",
  "settings.sso.withRole": "Met de rol",
  "settings.sso.noRole": "Geen rol (alleen wat iedereen mag zien)",
  "settings.sso.require": "Single sign-on verplichten: geen inloggen met wachtwoord voor mensen van de bewezen domeinen",
  "settings.sso.breakGlassNote": "Noodbeheerders (hieronder) houden hun wachtwoord en inloggen in twee stappen, voor als de identiteitsprovider onbereikbaar is.",
  "settings.sso.save": "Single sign-on opslaan",
  "settings.sso.domains": "Domeinen",
  "settings.sso.domainsIntro": "Bewijs een domein met een TXT-record: de adressen ervan loggen dan in (en koppelen) zonder wachtwoord",
  "settings.sso.domainsUntil": " — tot er een is bewezen, kunnen alleen mensen die vanuit hun profiel hebben gekoppeld single sign-on gebruiken",
  "settings.sso.proven": "Bewezen",
  "settings.sso.notProven": "Nog niet bewezen",
  "settings.sso.removeConfirm": "{domain} verwijderen? De mensen ervan kunnen dan alleen met single sign-on inloggen nadat ze het zelf hebben gekoppeld.",
  "settings.sso.domainRemoved": "Domein verwijderd",
  "settings.sso.copyName": "Naam van het record voor {domain} kopiëren",
  "settings.sso.copyValue": "Waarde van het record voor {domain} kopiëren",
  "settings.sso.domainAdded": "Domein toegevoegd",
  "settings.sso.domainToAdd": "Toe te voegen domein",
  "settings.sso.addDomain": "Domein toevoegen",
  "settings.sso.breakGlass": "Noodbeheerders",
  "settings.apikeys.revoked": "API-sleutel ingetrokken",
  "settings.apikeys.revokeFailed": "De sleutel kon niet worden ingetrokken",
  "settings.apikeys.copied": "Gekopieerd naar het klembord",
  "settings.apikeys.title": "API-sleutels",
  "settings.apikeys.subtitle": "Toegang tot uw account voor scripts en andere systemen",
  "settings.apikeys.create": "API-sleutel aanmaken",
  "settings.apikeys.createdTitle": "API-sleutel aangemaakt — kopieer hem nu",
  "settings.apikeys.createdText": "De volledige sleutel wordt alleen nu getoond. Bewaar hem op een veilige plek.",
  "settings.apikeys.hide": "Sleutel verbergen",
  "settings.apikeys.show": "Sleutel tonen",
  "settings.apikeys.dismiss": "Ik heb hem bewaard — sluiten",
  "settings.apikeys.prefix": "Voorvoegsel",
  "settings.apikeys.permissions": "Rechten",
  "settings.apikeys.lastUsed": "Laatst gebruikt",
  "settings.apikeys.expires": "Verloopt",
  "settings.apikeys.created": "Aangemaakt",
  "settings.apikeys.empty": "Nog geen API-sleutels",
  "settings.apikeys.revokedBadge": "Ingetrokken",
  "settings.apikeys.none": "Geen",
  "settings.apikeys.never": "Nooit",
  "settings.apikeys.revokeConfirm": "\"{name}\" intrekken? Dit kan niet ongedaan worden gemaakt.",
  "settings.apikeys.revoke": "Sleutel intrekken",
  "settings.apikeys.revokeNamed": "{name} intrekken",
  "settings.apikeys.nameRequired": "Een naam is verplicht",
  "settings.apikeys.createFailed": "De sleutel kon niet worden aangemaakt",
  "settings.apikeys.keyName": "Naam van de sleutel *",
  "settings.apikeys.keyNamePlaceholder": "bijv. CI/CD-pipeline, monitoringscript",
  "settings.apikeys.days30": "30 dagen",
  "settings.apikeys.days90": "90 dagen",
  "settings.apikeys.year1": "1 jaar",
  "settings.apikeys.permHint": "Kies de rechten die deze sleutel krijgt. Een sleutel heeft er minstens één nodig.",
  "settings.apikeys.createKey": "Sleutel aanmaken",
  "settings.branding.resetDone": "Huisstijl teruggezet naar de standaard",
  "settings.branding.resetFailed": "Terugzetten mislukt",
  "settings.branding.saved": "Huisstijl opgeslagen",
  "settings.branding.saveFailed": "De huisstijl kon niet worden opgeslagen",
  "settings.branding.title": "Huisstijl",
  "settings.branding.subtitle": "Het uiterlijk van het dashboard voor uw team",
  "settings.branding.resetConfirm": "Alle huisstijl terugzetten naar de standaard van Velanox? Hiermee verdwijnen alle eigen kleuren, lettertypen en logo’s.",
  "settings.branding.reset": "Terugzetten naar standaard",
  "settings.branding.save": "Huisstijl opslaan",
  "settings.branding.identity": "Identiteit",
  "settings.branding.appName": "Naam van de app",
  "settings.branding.logoUrl": "URL van het logo",
  "settings.branding.faviconUrl": "URL van het favicon",
  "settings.branding.loginBanner": "Bannerafbeelding bij inloggen",
  "settings.branding.colors": "Kleuren",
  "settings.branding.color.primaryColor": "Primair",
  "settings.branding.color.secondaryColor": "Secundair",
  "settings.branding.color.accentColor": "Accent",
  "settings.branding.color.backgroundColor": "Achtergrond",
  "settings.branding.color.sidebarColor": "Zijbalk",
  "settings.branding.color.textColor": "Tekst",
  "settings.branding.colourOf": "Kleur {name}",
  "settings.branding.colourCode": "Kleurcode {name}",
  "settings.branding.themeDefault": "standaard van het thema",
  "settings.branding.typography": "Typografie en vorm",
  "settings.branding.font": "Lettertype",
  "settings.branding.fontDefault": "Standaard van het platform (Geist)",
  "settings.branding.radius": "Hoekafronding",
  "settings.branding.radiusDefault": "Standaard van het platform",
  "settings.branding.radius.0": "Scherp (0px)",
  "settings.branding.radius.4": "Subtiel (4px)",
  "settings.branding.radius.8": "Zacht (8px)",
  "settings.branding.radius.12": "Afgerond (12px)",
  "settings.branding.radius.16": "Sterk afgerond (16px)",
  "settings.branding.customCss": "Eigen CSS",
  "settings.branding.customCssHint": "Geavanceerd: CSS die aan elke pagina wordt toegevoegd. Hooguit 50.000 tekens.",
  "settings.branding.cssPlaceholder": "/* Eigen CSS */",
  "settings.branding.preview": "Live voorbeeld",
  "settings.branding.applyPreview": "Voorbeeld toepassen",
  "settings.branding.sampleBadge": "Voorbeeldlabel",
  "settings.branding.previewHint": "Kies \"Voorbeeld toepassen\" om de wijzigingen live in de zijbalk te zien",
  "settings.messages.part.emailSubject": "Onderwerp van de e-mail",
  "settings.messages.part.emailBody": "Tekst van de e-mail",
  "settings.messages.part.whatsappBody": "WhatsApp-tekst",
  "settings.messages.part.rcsBody": "RCS-tekst",
  "settings.messages.wa.approved": "Goedgekeurd door WhatsApp: uw tekst wordt verstuurd",
  "settings.messages.wa.rejected": "Afgewezen door WhatsApp. De standaardtekst wordt verstuurd.",
  "settings.messages.wa.rejectedWhy": "Afgewezen door WhatsApp: {reason}. De standaardtekst wordt verstuurd.",
  "settings.messages.wa.failed": "Nog niet ingediend ({reason}). Elke 30 minuten opnieuw geprobeerd; tot die tijd wordt de standaardtekst verstuurd.",
  "settings.messages.wa.failedPlain": "Nog niet ingediend. Elke 30 minuten opnieuw geprobeerd; tot die tijd wordt de standaardtekst verstuurd.",
  "settings.messages.wa.paused": "Gepauzeerd door WhatsApp. De standaardtekst wordt verstuurd.",
  "settings.messages.wa.disabled": "Uitgeschakeld door WhatsApp. De standaardtekst wordt verstuurd.",
  "settings.messages.wa.pending": "Wacht op goedkeuring van WhatsApp (meestal binnen een dag). Tot die tijd wordt de standaardtekst verstuurd.",
  "settings.messages.notChanged": "Niet gewijzigd",
  "settings.messages.notSaved": "Niet opgeslagen",
  "settings.messages.tryAgain": "Probeer het zo opnieuw.",
  "settings.messages.title": "Berichten",
  "settings.messages.subtitle": "Wat uw mensen ontvangen per {channels}. Pas de tekst aan of zet een bericht uit.",
  "settings.messages.channel.email": "e-mail",
  "settings.messages.channels2": "{a} en {b}",
  "settings.messages.channels3": "{a}, {b} en {c}",
  "settings.messages.defaultsPending": "Uw eigen WhatsApp-account heeft de standaardteksten nog niet goedgekeurd: tot die tijd gaan WhatsApp-berichten als gewone tekst, die alleen aankomt binnen 24 uur na het laatste bericht van de ontvanger.",
  "settings.messages.variablesInfo": "Woorden tussen dubbele accolades, zoals {example}, worden ingevuld wanneer het bericht wordt verstuurd. Links komen alleen uit de linkvariabelen. Uitnodigingen en wachtwoordherstel gaan over beveiliging en worden dus altijd verstuurd.",
  "settings.messages.noLicence": "Uw licentie omvat het wijzigen van berichten niet. U kunt een bericht wel terugzetten naar de standaard.",
  "settings.messages.sentEmail": "Verstuurd per e-mail",
  "settings.messages.sentWhatsapp": "Verstuurd via WhatsApp",
  "settings.messages.sentRcs": "Verstuurd via RCS",
  "settings.messages.ownWording": "Eigen tekst",
  "settings.messages.alwaysSent": "Altijd verstuurd",
  "settings.messages.on": "Aan",
  "settings.messages.off": "Uit",
  "settings.messages.sendNamed": "\"{label}\" versturen",
  "settings.messages.resetConfirm": "\"{label}\" terugzetten naar de standaardtekst?",
  "settings.messages.resetDone": "Terug naar de standaard",
  "settings.messages.ownMeta": "Uw eigen WhatsApp loopt via Meta: de teksten zijn de sjablonen die zijn genoemd in Bedrijfsinstellingen → WhatsApp.",
  "settings.messages.twilioOnly": "WhatsApp-teksten kunnen worden gewijzigd wanneer het platform WhatsApp via Twilio verstuurt.",
  "settings.messages.waRules": "Eén alinea, woorden vóór de eerste variabele en na de laatste, nooit twee variabelen direct naast elkaar. Een gewijzigde tekst heeft goedkeuring van WhatsApp nodig voordat hij wordt gebruikt.",
  "settings.messages.rcsNote": "Komt aan in de berichtenapp van de telefoon zelf. Direct in gebruik: RCS-teksten hebben geen goedkeuring nodig.",
  "settings.messages.insertVariable": "Een variabele invoegen",
  "settings.messages.example": "Voorbeeld: {value}",
  "settings.messages.required": "* moet in de tekst van de e-mail blijven",
  "settings.messages.save": "Opslaan",
  "settings.messages.backToDefault": "Terug naar standaard",
  "settings.messages.previewRegion": "Voorbeeld met voorbeeldwaarden",
  "settings.messages.preview": "Voorbeeld",
  "settings.vpn.downloadFailed": "Downloaden mislukt",
  "settings.vpn.mayBeCreating": "Het VPN-bestand wordt mogelijk nog aangemaakt.",
  "settings.vpn.creating": "VPN-account aangevraagd",
  "settings.vpn.creatingText": "Uw VPN-account wordt aangemaakt. Vernieuw over 30 seconden.",
  "settings.vpn.createFailed": "Het VPN-account kon niet worden aangemaakt",
  "settings.vpn.contactAdmin": "Neem contact op met uw beheerder.",
  "settings.vpn.regenerated": "Nieuw VPN-bestand aangemaakt",
  "settings.vpn.regeneratedText": "Download hieronder uw nieuwe bestand.",
  "settings.vpn.regenerateFailed": "Er kon geen nieuw VPN-bestand worden aangemaakt",
  "settings.vpn.memberConfirm": "Een nieuw VPN-bestand aanmaken voor \"{name}\"? Het huidige werkt dan niet meer.",
  "settings.vpn.memberDone": "VPN-account aangevraagd",
  "settings.vpn.memberDoneText": "Er wordt een nieuw VPN-bestand aangemaakt voor {name}.",
  "settings.vpn.suspendConfirm": "De VPN van \"{name}\" opschorten?",
  "settings.vpn.enableConfirm": "De VPN van \"{name}\" weer inschakelen?",
  "settings.vpn.suspended": "VPN opgeschort",
  "settings.vpn.enabled": "VPN ingeschakeld",
  "settings.vpn.notChanged": "Niet gewijzigd",
  "settings.vpn.siteRegenerated": "Nieuw VPN-bestand aangemaakt voor de locatie",
  "settings.vpn.subtitle": "OpenVPN: een apart netwerk voor elk bedrijf",
  "settings.vpn.mine": "Mijn VPN-account",
  "settings.vpn.lastSeen": "Laatst gezien",
  "settings.vpn.download": ".ovpn downloaden",
  "settings.vpn.regenerateMineConfirm": "Een nieuw VPN-bestand aanmaken? Uw huidige .ovpn-bestand werkt dan niet meer.",
  "settings.vpn.regenerateHint": "Nieuw bestand: als het huidige kwijt is of door anderen gezien kan zijn",
  "settings.vpn.regenerate": "Certificaat vernieuwen",
  "settings.vpn.suspendedMine": "Uw VPN-account is opgeschort. Neem contact op met uw beheerder.",
  "settings.vpn.noAccount": "Nog geen VPN-account.",
  "settings.vpn.createMine": "Mijn VPN-account aanmaken",
  "settings.vpn.network": "VPN-netwerk van het bedrijf",
  "settings.vpn.usersNet": "Netwerk voor gebruikers",
  "settings.vpn.sitesNet": "Netwerk voor locaties",
  "settings.vpn.isolation": "Gebruikers kunnen elke locatie van het bedrijf bereiken. Locaties kunnen elkaar niet bereiken.",
  "settings.vpn.networkPending": "Het VPN-netwerk wordt ingericht…",
  "settings.vpn.all": "Alle VPN-accounts",
  "settings.vpn.count": { one: "{count} account", other: "{count} accounts" },
  "settings.vpn.account": "Account",
  "settings.vpn.type": "Type",
  "settings.vpn.type.USER": "Persoon",
  "settings.vpn.type.SITE": "Locatie",
  "settings.vpn.empty": "Nog geen VPN-accounts",
  "settings.vpn.downloadSite": "VPN-bestand van de locatie downloaden",
  "settings.vpn.siteConfirm": "Een nieuw VPN-bestand aanmaken voor \"{name}\"? Het huidige werkt dan niet meer.",
  "settings.vpn.suspend": "VPN opschorten",
  "settings.vpn.enable": "VPN inschakelen",
  "settings.vpn.createFor": "VPN-account aanmaken",
  "settings.vpn.setup": "Instructies",
  "settings.vpn.tab.personal": "Eigen apparaat",
  "settings.vpn.tab.unifi": "UniFi OS (router van de locatie)",
  "settings.vpn.pc.1": "Download OpenVPN Connect via https://openvpn.net/client/",
  "settings.vpn.pc.2": "Kies + en daarna \"Upload File\"",
  "settings.vpn.pc.3": "Selecteer uw .ovpn-bestand",
  "settings.vpn.pc.4": "Kies Connect — geen gebruikersnaam of wachtwoord nodig",
  "settings.vpn.pc.note": "OpenVPN Connect 3.x is vereist. De oudere OpenVPN GUI werkt ook.",
  "settings.vpn.linux.1": "Installeren: sudo apt install openvpn (of het equivalent voor uw distributie)",
  "settings.vpn.linux.2": "Verbinden: sudo openvpn --config uw-naam.ovpn",
  "settings.vpn.linux.3": "Of gebruik NetworkManager: importeer het .ovpn-bestand via het netwerkmenu",
  "settings.vpn.ios.1": "Installeer \"OpenVPN Connect\" uit de App Store",
  "settings.vpn.ios.2": "Stuur het .ovpn-bestand via AirDrop of e-mail naar uw apparaat",
  "settings.vpn.ios.3": "Kies \"Openen met OpenVPN Connect\" wanneer daarom wordt gevraagd",
  "settings.vpn.ios.4": "Kies Add en daarna Connect",
  "settings.vpn.android.1": "Installeer \"OpenVPN Connect\" uit de Play Store",
  "settings.vpn.android.2": "Deel het .ovpn-bestand met OpenVPN Connect",
  "settings.vpn.android.3": "Kies Import en daarna Connect",
  "settings.vpn.security": "Beveiliging:",
  "settings.vpn.securityText": "Houd uw .ovpn-bestand privé: het is uw persoonlijke toegang. Is het kwijt of kan het door anderen gezien zijn, maak dan hierboven een nieuw bestand aan; het oude werkt dan niet meer.",
  "settings.webhooks.event.site.offline": "Locatie offline",
  "settings.webhooks.event.site.online": "Locatie weer online",
  "settings.webhooks.event.alert.critical": "Kritieke meldingen",
  "settings.webhooks.event.alert.error": "Foutmeldingen",
  "settings.webhooks.event.alert.warning": "Waarschuwingen",
  "settings.webhooks.event.alert.info": "Informatie",
  "settings.webhooks.event.license.expiring": "Licentie verloopt",
  "settings.webhooks.deleted": "Webhook verwijderd",
  "settings.webhooks.deleteFailed": "De webhook kon niet worden verwijderd",
  "settings.webhooks.toggleFailed": "De webhook kon niet worden omgezet",
  "settings.webhooks.testDelivered": "Test afgeleverd",
  "settings.webhooks.answered": "Uw endpoint antwoordde {code}.",
  "settings.webhooks.testFailed": "Test mislukt",
  "settings.webhooks.noResponse": "Geen antwoord",
  "settings.webhooks.unreachable": "Controleer of de URL van de webhook bereikbaar is en antwoordt met een 2xx-status.",
  "settings.webhooks.title": "Webhooks",
  "settings.webhooks.subtitle": "Gebeurtenissen die direct naar uw eigen systemen worden gestuurd, via HTTP POST",
  "settings.webhooks.add": "Webhook toevoegen",
  "settings.webhooks.eventsLabel": "Gebeurtenissen:",
  "settings.webhooks.info": "Elke webhook ontvangt de gebeurtenissen die ervoor zijn gekozen (allemaal als er geen zijn gekozen). Payloads worden ondertekend met HMAC-SHA256 en uw webhookgeheim.",
  "settings.webhooks.events": "Gebeurtenissen",
  "settings.webhooks.updated": "Laatst bijgewerkt",
  "settings.webhooks.empty": "Nog geen webhooks",
  "settings.webhooks.allEvents": "Alle gebeurtenissen",
  "settings.webhooks.disable": "Uitschakelen",
  "settings.webhooks.enable": "Inschakelen",
  "settings.webhooks.sendTest": "Testgebeurtenis versturen",
  "settings.webhooks.edit": "Bewerken",
  "settings.webhooks.deleteConfirm": "Webhook \"{name}\" verwijderen?",
  "settings.webhooks.nameRequired": "Een naam is verplicht",
  "settings.webhooks.urlRequired": "Een URL is verplicht",
  "settings.webhooks.saveFailed": "De webhook kon niet worden opgeslagen",
  "settings.webhooks.editTitle": "Webhook bewerken",
  "settings.webhooks.name": "Naam *",
  "settings.webhooks.namePlaceholder": "Mijn webhook",
  "settings.webhooks.secret": "Geheim",
  "settings.webhooks.secretKeep": "(laat leeg om het huidige te houden)",
  "settings.webhooks.secretRecommended": "(aanbevolen)",
  "settings.webhooks.secretPlaceholder": "Optioneel geheim voor de HMAC-handtekening",
  "settings.webhooks.signed": "Als het is ingesteld, wordt elke payload ondertekend in deze header:",
  "settings.webhooks.noneChosen": "(niets gekozen: allemaal)",
  "settings.webhooks.saveChanges": "Wijzigingen opslaan",
  "settings.messages.who": "Wie krijgt deze tekst",
  "settings.messages.who.everyone": "Iedereen, in welke taal ook",
  "settings.messages.who.perLanguage": "Per taal — anderen krijgen de standaardtekst in hun taal",
  "settings.messages.language": "Taal",
  "settings.messages.hasOwnText": "heeft een eigen tekst",
  "settings.messages.everyoneHint": "Eén keer geschreven en naar iedereen gestuurd zoals het is. Delen die u niet wijzigt, volgen de taal van ieder.",
  "settings.messages.languageHint": "Wie {language} leest, krijgt deze tekst; tot u hem wijzigt, staat hier de standaardtekst in het {language}.",
  "settings.messages.removeLanguage": "De tekst in het {language} verwijderen",
  "settings.messages.removeLanguageConfirm": "De tekst in het {language} van “{label}” verwijderen? Wie {language} leest, krijgt dan de standaardtekst.",
  "settings.messages.removed": "Tekst verwijderd",
  "settings.tabs": "Onderdelen van de instellingen",
  "settings.tab.general": "Algemeen",
  "settings.tab.security": "Beveiliging",
  "settings.tab.monitoring": "Monitoring en meldingen",
  "settings.tab.messaging": "Berichten",
  "settings.tab.licence": "Licentie",
  "settings.unsaved": "niet-opgeslagen wijzigingen",
  "settings.languageTime": "Taal en tijdzone",
  "settings.messaging.none": "Uw licentie bevat geen eigen mailserver, WhatsApp of RCS: berichten gaan via het platform.",
  "settings.logo.title": "Logo",
  "settings.logo.alt": "Logo van {name}",
  "settings.logo.hint": "Toont het bedrijf waar u het kiest na het inloggen, en in lijsten. Elke afbeelding (PNG, JPEG, WebP, GIF, SVG, AVIF…), tot 10 MB; het wordt verkleind tot 256 × 256.",
  "settings.logo.choose": "Afbeelding kiezen",
  "settings.logo.change": "Andere afbeelding",
  "settings.logo.remove": "Logo verwijderen",
  "settings.logo.removeConfirm": "Het logo van {name} verwijderen?",
  "settings.logo.saved": "Logo opgeslagen",
  "settings.logo.removed": "Logo verwijderd",
  "settings.logo.notSaved": "Logo niet opgeslagen",
  "settings.logo.wrongType": "Kies een afbeeldingsbestand",
  "settings.logo.tooLarge": "De afbeelding is groter dan 10 MB",
  "settings.logo.unreadable": "Deze browser kan deze afbeelding niet openen: sla haar op als PNG of JPEG en probeer het opnieuw",
  "settings.banner.title": "Bannerlogo",
  "settings.banner.hint": "Staat midden in de bovenbalk in plaats van de bedrijfsnaam. Een brede afbeelding, van elke soort (PNG, JPEG, WebP, GIF, SVG, AVIF…), tot 10 MB.",
  "settings.banner.remove": "Bannerlogo verwijderen",
  "settings.banner.removeConfirm": "Het bannerlogo van {name} verwijderen?",
  "settings.banner.saved": "Bannerlogo opgeslagen",
  "settings.banner.removed": "Bannerlogo verwijderd",
  "settings.banner.notSaved": "Bannerlogo niet opgeslagen",
  "settings.tab.rental": "Verhuur",
  "settings.tab.connections": "Verbindingen",
  "settings.connections.intro": "De controllers waarlangs je locaties bereikt worden. Elke verbinding wordt gecontroleerd bij het bewaren; een geheim wordt nooit meer getoond.",
  "settings.connections.none": "Nog geen verbindingen: voeg er een toe via de pagina Locaties.",
  "settings.connections.saved": "Verbinding bewaard",
  "settings.connections.saveFailed": "Bewaren van de verbinding mislukt",
  "settings.connections.deleted": "Verbinding verwijderd",
  "settings.connections.deleteFailed": "Verwijderen van de verbinding mislukt",
  "settings.connections.sites": { one: "{count} locatie", other: "{count} locaties" },
  "settings.connections.checked": "gecontroleerd {when}",
  "settings.connections.waiting": "Wacht op de VPN-verbinding van de controller: download de .ovpn en start hem op de computer van de controller.",
  "settings.connections.save": "Bewaren",
  "settings.connections.addSites": "Locaties toevoegen",
  "settings.connections.edit": "Bewerken",
  "settings.connections.deleteConfirm": "De verbinding {name} verwijderen?",
  "settings.connections.newCert": "Nieuw certificaat",
  "settings.connections.newCertDone": "Nieuw certificaat uitgegeven: download de .ovpn en start hem op de computer van de controller",
  "settings.connections.newCertFailed": "Uitgeven van een nieuw certificaat mislukt",
  "settings.phonesStay": "Telefoons mogen aangemeld blijven (de mobiele app, geopend met Face ID of een vingerafdruk)",
  "settings.phonesStayHelp": "De inactiviteitslimiet hierboven telt ook de tijd in de app; daarna vraagt de telefoon opnieuw om Face ID.",
  "settings.phonesOffConfirm": "Dit uitzetten? Na opslaan wordt elke telefoon van het bedrijf afgemeld en vraagt die opnieuw om het wachtwoord.",
  "settings.messages.part.pushTitle": "Titel pushmelding",
  "settings.messages.part.pushBody": "Tekst pushmelding",
  "settings.messages.sentPush": "Verstuurd als pushmelding (telefoon-app)",
  "settings.messages.pushNote": "Op het vergrendelscherm van de telefoon getoond onder de naam van de Velanox-app; maximaal 100 en 500 tekens.",
  "settings.messages.pushPreview": "Velanox-app · pushmelding",
  "settings.simPins.title": "Simkaarten",
  "settings.simPins.help": "De bedrijfspincode voor de simkaarten in Teltonika-routers, en de simkaarten met een eigen pincode. Pincodes worden als stippen getoond; Tonen vraagt om uw wachtwoord en wordt vastgelegd.",
  "settings.simPins.companyPin": "Bedrijfspincode",
  "settings.simPins.notSet": "Niet ingesteld",
  "settings.simPins.set": "Instellen",
  "settings.simPins.save": "Opslaan",
  "settings.simPins.newCompanyPin": "Nieuwe bedrijfspincode (4 tot 8 cijfers)",
  "settings.simPins.changeHelp": "Simkaarten die al met de huidige bedrijfspincode zijn vergrendeld, houden die als eigen pincode.",
  "settings.simPins.show": "Tonen",
  "settings.simPins.ownPins": "Simkaarten met een eigen pincode",
  "settings.simPins.noOwn": "Nog geen.",
  "settings.simPins.removeConfirm": "De pincode van simkaart {iccid} vergeten?",
  "settings.simPins.notShown": "De pincode is niet getoond",
  "settings.simPins.notSaved": "Niet opgeslagen",
  "settings.simPins.removeNoteConfirm": "De notitie van simkaart {iccid} vergeten? Hij blijft op de bedrijfspincode.",
  "settings.pushFirst.title": "Eerst push",
  "settings.pushFirst.companyIntro": "Geldt voor de eigen WhatsApp en RCS van dit bedrijf.",
  "settings.pushFirst.on": "Push gaat eerst",
  "settings.pushFirst.onHelp": "Een melding gaat per push naar wie de app heeft; WhatsApp of RCS volgt alleen als die niet binnen de wachttijd is gezien.",
  "settings.pushFirst.wait": "Wachttijd vóór WhatsApp of RCS (minuten)",
  "settings.pushFirst.waitRange": "1 tot 60 hele minuten",
  "settings.pushFirst.anyone": "WhatsApp en RCS overslaan zodra iemand het heeft gezien",
  "settings.pushFirst.anyoneHelp": "Een collega die de locatie opent, telt voor iedereen die erover is ingelicht.",
  "settings.pushFirst.save": "Opslaan",
  "settings.backdrop.dark": "Achtergrond in donkere modus",
  "settings.backdrop.light": "Achtergrond in lichte modus",
  "settings.backdrop.none": "Geen",
  "settings.backdrop.default.dark": "Warm wit",
  "settings.backdrop.default.light": "Dashboard-donker",
  "settings.backdrop.own": "Eigen kleur",
  "settings.backdrop.colour.dark": "Achtergrondkleur in donkere modus",
  "settings.backdrop.colour.light": "Achtergrondkleur in lichte modus",
  "settings.backdrop.saved": "Achtergrond opgeslagen",
  "settings.backdrop.notSaved": "Achtergrond niet opgeslagen",
  "settings.backdrop.help": "Voor een afbeelding die in één modus wegvalt: een lichte achtergrond achter donkere letters, een donkere achter lichte letters.",
}

export default settings
